import { UnitCode, type Quantity } from '~/graphql/graphql';
import type { ProductDimensionsInches } from './order';
import { quantityInUnit, type CartQuantity } from './targetOrderSize';

type IdentifiedEntity = { id: string } | null | undefined;

export type CartItemForQuantity = {
  platformProductsLengths?: {
    length?: IdentifiedEntity;
    platformProduct?: IdentifiedEntity;
  } | null;
  tallyPlatformProduct?: IdentifiedEntity;
  quantity: Quantity;
};

export type CartForQuantity = {
  items?: CartItemForQuantity[] | null;
};

function areUnitsInterchangeable(
  fromUnit: UnitCode,
  toUnit: UnitCode
): boolean {
  if (fromUnit === toUnit) return true;
  return fromUnit !== UnitCode.Tallies && toUnit !== UnitCode.Tallies;
}

/**
 * Total quantity the carts already hold for the product length, restated in the
 * unit of the first matching cart line. Reports `empty` when nothing matches and
 * `unmeasurable` when a matching line carries a unit the product dimensions
 * cannot reconcile, so a caller never counts an unreadable line as zero.
 *
 * @example
 * cartQuantityForProductLength(carts, 'length-8ft', 'product-2x4', {
 *   lengthInches: 96,
 *   widthInches: 3.5,
 *   thicknessInches: 1.5,
 * });
 */
export function cartQuantityForProductLength(
  carts: readonly CartForQuantity[] | null | undefined,
  lengthId: string | null | undefined,
  platformProductId: string | null | undefined,
  dims: ProductDimensionsInches
): CartQuantity {
  if (!lengthId || !platformProductId) return { kind: 'empty' };

  const matchingItems = (carts ?? [])
    .flatMap((cart) => cart.items ?? [])
    .filter((item) => {
      if (item.platformProductsLengths?.length?.id !== lengthId) return false;
      return (
        item.platformProductsLengths?.platformProduct?.id ===
          platformProductId ||
        item.tallyPlatformProduct?.id === platformProductId
      );
    });

  if (matchingItems.length === 0) return { kind: 'empty' };

  const cartUnit = matchingItems[0].quantity.unit;
  let totalInCartUnit = 0;
  for (const item of matchingItems) {
    if (!areUnitsInterchangeable(item.quantity.unit, cartUnit)) {
      return { kind: 'unmeasurable' };
    }
    const quantityInCartUnit = quantityInUnit(item.quantity, cartUnit, dims);
    if (quantityInCartUnit === null) return { kind: 'unmeasurable' };
    totalInCartUnit += quantityInCartUnit;
  }

  return {
    kind: 'measured',
    quantity: { value: totalInCartUnit, unit: cartUnit },
  };
}
